// +----------------------------------------------------------------------
// | 首页（绝对核心）：输入纠结 → 帮我想想
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DecisionInput from '../../components/DecisionInput';
import ModeSwitch from '../../components/ModeSwitch';
import { toast } from '../../components/Toast';
import { createDecision, listDecisions } from '../../api/decision';
import { hotTopics } from '../../api/topic';
import useAuth from '../../hooks/useAuth';
import { categoryEmoji, categoryText, statusLabel } from '../../lib/categoryMap';
import { fromNow, greeting } from '../../lib/format';
import './Home.less';

// 不知道怎么写？点一下填进去（降低「不会提问」的门槛）
const SAMPLE_QUESTIONS = [
  '周末去爬山还是在家？',
  '这个相机要不要买？',
  '晚上吃火锅还是烧烤？',
  '这个项目我要不要接？',
];

export default function Home() {
  const [question, setQuestion] = useState('');
  const [mode, setMode] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);
  const [showTopics, setShowTopics] = useState(false);

  // 热门纠结：点开才拉，省掉首页的一次请求
  const [hot, setHot] = useState(null);
  const [hotLoading, setHotLoading] = useState(false);

  const { isLoggedIn, user, stats, refreshStats } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 从话题详情页带问题回来：/?ask=要不要换工作
  useEffect(() => {
    const ask = searchParams.get('ask');
    if (ask) {
      setQuestion(ask);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadRecent = useCallback(async () => {
    if (!isLoggedIn) {
      setRecent([]);
      return;
    }
    try {
      const data = await listDecisions({ page: 1, page_size: 5 });
      const list = (data && (data.list || data.items || data.data)) || [];
      setRecent(Array.isArray(list) ? list : []);
    } catch (e) {
      // 决策接口（Phase 3）还没就绪时静默处理，不打扰用户
      setRecent([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const handleSubmit = async (text) => {
    if (submitting) return;

    if (!isLoggedIn) {
      toast('先登录一下，我才好帮你记住这些纠结');
      navigate('/login', { state: { from: '/' } });
      return;
    }

    setSubmitting(true);
    try {
      const data = await createDecision({ question: text, mode });
      setQuestion('');
      refreshStats();
      if (data && data.id) {
        navigate(`/decision/${data.id}`);
      }
    } catch (e) {
      toast(e.message || '没想出来，再试一次', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const fillQuestion = (text) => {
    setQuestion(text);
    setShowTopics(false);
  };

  // 展开热门时按需拉一次（公共数据，免登录也能看）
  const handleToggleTopics = async () => {
    const next = !showTopics;
    setShowTopics(next);
    if (!next || hot !== null || hotLoading) {
      return;
    }
    setHotLoading(true);
    try {
      setHot(await hotTopics({ days: 30 }));
    } catch (e) {
      setHot({ topics: [], categories: [], sample: 0, decided: 0 });
    } finally {
      setHotLoading(false);
    }
  };

  const hotList = (hot && hot.topics) || [];
  const hotTop = hotList[0];

  return (
    <div className="page home">
      <section className="home-hero">
        <h1 className="home-title">想想</h1>
        <p className="home-slogan">有啥纠结的，扔过来。</p>
        {isLoggedIn && user && (
          <p className="home-greeting muted">
            {greeting()}
            {user.nickname ? `，${user.nickname}` : ''}
          </p>
        )}
      </section>

      <section className="home-input fade-in-up">
        <DecisionInput
          value={question}
          onChange={setQuestion}
          onSubmit={handleSubmit}
          loading={submitting}
        />

        <div className="home-mode">
          <ModeSwitch value={mode} onChange={setMode} disabled={submitting} />
          <span className="home-mode-hint muted">
            {mode === 'quick' && '只问几个关键问题，快速给倾向'}
            {mode === 'normal' && '必要时我会先问你几个关键问题'}
            {mode === 'random' && '这是娱乐模式，不是理性决策'}
          </span>
        </div>
      </section>

      <section className="home-samples">
        <span className="home-samples-label muted">不知道怎么问？</span>
        <div className="home-chips">
          {SAMPLE_QUESTIONS.map((text) => (
            <button key={text} type="button" className="home-chip" onClick={() => fillQuestion(text)}>
              {text}
            </button>
          ))}
        </div>
      </section>

      <section className="home-entries">
        <button
          type="button"
          className={`home-entry ${mode === 'random' ? 'is-active' : ''}`}
          onClick={() => setMode('random')}
        >
          <span className="home-entry-emoji">🎲</span>
          <span>随便吧</span>
        </button>

        <Link to="/chat" className="home-entry">
          <span className="home-entry-emoji">💬</span>
          <span>和想想聊聊</span>
        </Link>

        <button
          type="button"
          className={`home-entry ${showTopics ? 'is-active' : ''}`}
          onClick={handleToggleTopics}
        >
          <span className="home-entry-emoji">🔥</span>
          <span>大家最近在纠结什么</span>
        </button>
      </section>

      {showTopics && (
        <section className="card home-topics fade-in-up">
          <div className="home-card-head">
            <h3>最近热门</h3>
            <Link to="/topics" className="muted">
              完整榜单 ›
            </Link>
          </div>

          {hotLoading && <p className="muted home-topics-note">正在数一数…</p>}

          {!hotLoading && hotList.length > 0 && (
            <>
              <div className="home-chips">
                {hotList.slice(0, 6).map((topic) => (
                  <button
                    key={topic.key}
                    type="button"
                    className="home-chip"
                    onClick={() => fillQuestion(topic.label)}
                  >
                    {topic.emoji} {topic.label}
                  </button>
                ))}
              </div>

              {hotTop && (
                <p className="home-topics-lead">
                  {hotTop.percent_available ? (
                    <>
                      最热的是<strong>「{hotTop.label}」</strong>——
                      {hotTop.count} 人想过，其中 {hotTop.decided} 人做了选择，
                      <strong>{hotTop.top_percent}%</strong> 最后选了「{hotTop.top_option}」。
                    </>
                  ) : (
                    <>
                      最热的是<strong>「{hotTop.label}」</strong>——{hotTop.count} 人想过，
                      其中 {hotTop.decided} 人做出了选择。
                    </>
                  )}
                </p>
              )}

              <p className="muted home-topics-note">
                这里只有匿名数字：多少人想过、最后怎么选的。没有别人的具体问题，也没有 AI 记住的个人信息。
              </p>
            </>
          )}

          {!hotLoading && hotList.length === 0 && (
            <p className="muted home-topics-note">
              榜单还没攒够数据。要不你先扔一件纠结的事过来？
            </p>
          )}
        </section>
      )}

      {isLoggedIn && stats && (
        <section className="home-stats">
          <div className="home-stat">
            <strong>{stats.total || 0}</strong>
            <span>我的决定</span>
          </div>
          <div className="home-stat">
            <strong>{stats.decided || 0}</strong>
            <span>已决定</span>
          </div>
          <div className="home-stat">
            <strong>{stats.feedback || 0}</strong>
            <span>反馈结果</span>
          </div>
        </section>
      )}

      <section className="home-recent">
        <div className="home-card-head">
          <h3>最近的决定</h3>
          {isLoggedIn && recent.length > 0 && (
            <Link to="/history" className="muted">
              全部 ›
            </Link>
          )}
        </div>

        {recent.length > 0 ? (
          <ul className="home-recent-list">
            {recent.map((item) => (
              <li key={item.id}>
                <Link to={`/decision/${item.id}`} className="home-recent-item">
                  <span className="home-recent-emoji">{categoryEmoji(item.category)}</span>
                  <span className="home-recent-body">
                    <span className="home-recent-title">{item.title || item.question}</span>
                    <span className="muted">
                      {categoryText(item.category)}
                      {item.created_at ? ` · ${fromNow(item.created_at)}` : ''}
                      {item.status ? ` · ${statusLabel(item.status)}` : ''}
                    </span>
                  </span>
                  <span className="home-recent-arrow">›</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="home-empty">
            {isLoggedIn ? (
              <>
                <p className="muted">还没有记录。上面那件纠结的事，说给我听听？</p>
              </>
            ) : (
              <>
                <p className="muted">登录后，我做过的分析和你的决定都会存在这里。</p>
                <div className="home-empty-actions">
                  <Link to="/login" className="btn btn-primary btn-sm">
                    登录
                  </Link>
                  <Link to="/register" className="btn btn-ghost btn-sm">
                    注册
                  </Link>
                </div>
              </>
            )}
          </div>
        )}
      </section>

      <footer className="home-footer muted">
        想想只帮你把事情想清楚，最后的决定永远是你的。
      </footer>
    </div>
  );
}
